import type { WireSourceHealthRow, WireSourceInstance, WireSourceKind } from '@logistics/contracts';

/**
 * What the panel shows of a source's configuration. A secret is never sent to
 * the panel (docs/adr/0001, "masked only"), so there is nothing to reveal or
 * copy: a stored one reads "set", a missing one "not set".
 */
export interface CredentialRow {
  readonly name: string;
  readonly label: string;
  readonly display: string;
  readonly secret: boolean;
}

export const MASK = '••••••••';

export function credentialRows(
  descriptor: WireSourceKind | undefined,
  instance: WireSourceInstance,
): CredentialRow[] {
  if (descriptor === undefined) {
    return instance.secretsPresent.map((name) => secretRow(name, name, true));
  }
  const rows: CredentialRow[] = [];
  for (const field of descriptor.fields) {
    if (field.secret) {
      rows.push(secretRow(field.name, field.label, instance.secretsPresent.includes(field.name)));
      continue;
    }
    const value = settingText(instance.settings[field.name]);
    if (value !== undefined) {
      rows.push({ name: field.name, label: field.label, display: value, secret: false });
    }
  }
  return rows;
}

function secretRow(name: string, label: string, present: boolean): CredentialRow {
  return { name, label, display: present ? `${MASK}  set` : 'not set', secret: true };
}

function settingText(value: unknown): string | undefined {
  if (typeof value === 'string') return value === '' ? undefined : value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'on' : 'off';
  return undefined;
}

/** "{n} credentials" on a card: the descriptor's secret fields. */
export function secretCount(descriptor: WireSourceKind | undefined): number {
  return descriptor?.fields.filter((f) => f.secret).length ?? 0;
}

export interface SourceStatus {
  readonly color: string;
  readonly label: string;
  readonly failing: boolean;
}

/**
 * The card's dot and word. Health knows best; before it has a row for a new
 * instance, the instance itself says whether it is paused or failing.
 */
export function sourceStatus(
  instance: WireSourceInstance,
  health: WireSourceHealthRow | undefined,
): SourceStatus {
  const status =
    health?.status ??
    (!instance.enabled ? 'disabled' : instance.lastError !== undefined ? 'error' : 'pending');
  switch (status) {
    case 'ok':
      return { color: 'var(--ok)', label: 'Connected', failing: false };
    case 'error':
      return { color: 'var(--bad)', label: 'Auth error', failing: true };
    case 'disabled':
      return { color: 'var(--mute)', label: 'Paused', failing: false };
    case 'pending':
      return { color: 'var(--mute)', label: 'Pending', failing: false };
  }
}
